import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useSalesforce } from '../hooks/useSalesforce';
import { useAuth } from '../hooks/useAuth';
import WorkspaceModal from './WorkspaceModal';
import { ChevronDownIcon, ChevronUpIcon, SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { convertMarkdownToHTML } from '../utils/markdownUtils';

interface WorkspaceManagementProps {
  orgId: string;
  accountId: string;
  subscriptionTier: string;
}

interface WorkspaceOverviewData {
  workspaceId: string;
  status: string;
  content: string | null;
  keyInsights: any[] | null;
  generatedAt: string | null;
  error: string | null;
  flowCount: number;
}

export default function WorkspaceManagement({ orgId, accountId, subscriptionTier }: WorkspaceManagementProps) {
  const { workspaces, activateWorkspace, deactivateWorkspace, deleteWorkspace, updateWorkspaceOrder, isLoading } = useWorkspace();
  const { isSuperAdmin, userRole } = useSalesforce({ skipInitialFetch: true });
  const { user, supabase } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [draggedWorkspace, setDraggedWorkspace] = useState<string | null>(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);
  const [localWorkspaces, setLocalWorkspaces] = useState<any[]>([]);
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set());
  const [overviewData, setOverviewData] = useState<Record<string, WorkspaceOverviewData>>({});
  const [generatingOverview, setGeneratingOverview] = useState<Set<string>>(new Set());
  const [token, setToken] = useState<string>('');

  // Get token for API calls
  useEffect(() => {
    const getToken = async () => {
      if (!user) return;
      
      try {
        // Get the session token
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setToken(session.access_token);
        }
      } catch (error) {
        console.error('Error getting token:', error);
      }
    };
    
    getToken();
  }, [user, supabase]);

  // Sync local state with workspace changes - only show default workspace
  useEffect(() => {
    const filtered = workspaces
      .filter(w => w.org_id === orgId && w.is_default)
      .sort((a, b) => {
        // Sort by display_order if available, otherwise fall back to default first, then name
        if (a.display_order !== undefined && b.display_order !== undefined) {
          return a.display_order - b.display_order;
        }
        // Fallback sorting
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return a.name.localeCompare(b.name);
      });
    setLocalWorkspaces(filtered);
  }, [workspaces, orgId]);

  // Fetch overview data for all workspaces
  useEffect(() => {
    const fetchOverviewData = async () => {
      if (!token || localWorkspaces.length === 0) return;

      for (const workspace of localWorkspaces) {
        if (!overviewData[workspace.id]) {
          try {
            const response = await fetch(`/api/workspaces/overview-status/${workspace.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                setOverviewData(prev => ({
                  ...prev,
                  [workspace.id]: data
                }));
              } else {
                console.warn(`Overview endpoint returned non-JSON response for workspace ${workspace.id}`);
              }
            } else if (response.status === 404) {
              console.warn('Overview API endpoints not found. Please deploy the workspace overview API endpoints.');
            } else if (response.status === 500) {
              const text = await response.text();
              console.error(`Server error for workspace ${workspace.id}:`, text);
              // Don't throw error, just log it - the feature is optional
            }
          } catch (error) {
            console.error(`Error fetching overview for workspace ${workspace.id}:`, error);
          }
        }
      }
    };

    fetchOverviewData();
  }, [localWorkspaces, token]);

  const defaultWorkspace = localWorkspaces.find(w => w.is_default);
  const customWorkspaces = localWorkspaces.filter(w => !w.is_default);
  
  // Count active workspaces to determine if default can be deactivated
  const activeWorkspaceCount = localWorkspaces.filter(w => w.is_active).length;
  
  // Basic tier gets 1 default workspace only
  // Pro/Enterprise can add additional workspaces
  const canAddWorkspace = subscriptionTier !== 'basic' && isSuperAdmin;

  const handleActivate = async (workspaceId: string) => {
    try {
      await activateWorkspace(workspaceId);
    } catch (error) {
      console.error('Failed to activate workspace:', error);
      alert('Failed to activate workspace. Please try again.');
    }
  };

  const handleDeactivate = async (workspaceId: string) => {
    if (!confirm('Are you sure you want to deactivate this workspace? This will cancel all pending AI analyses.')) {
      return;
    }

    try {
      await deactivateWorkspace(workspaceId);
    } catch (error) {
      console.error('Failed to deactivate workspace:', error);
      alert('Failed to deactivate workspace. Please try again.');
    }
  };

  const handleDelete = async (workspaceId: string) => {
    if (!confirm('Are you sure you want to delete this workspace? This action cannot be undone and will delete all associated summaries.')) {
      return;
    }

    setIsDeleting(workspaceId);
    try {
      await deleteWorkspace(workspaceId);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      alert('Failed to delete workspace. Please try again.');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleEdit = (workspace: any) => {
    setEditingWorkspace(workspace);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingWorkspace(null);
    setShowModal(true);
  };

  // Check if user can drag - for now, only super admins can manage workspaces
  const canDrag = isSuperAdmin;

  const handleDragStart = (e: React.DragEvent, workspaceId: string, index: number) => {
    setDraggedWorkspace(workspaceId);
    e.dataTransfer.effectAllowed = 'move';
    // Store the dragged item index
    e.dataTransfer.setData('dragIndex', index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    if (draggedWorkspace && draggedOverIndex !== index) {
      setDraggedOverIndex(index);
      
      // Real-time reordering
      const draggedIndex = localWorkspaces.findIndex(w => w.id === draggedWorkspace);
      if (draggedIndex !== -1 && draggedIndex !== index) {
        const newWorkspaces = [...localWorkspaces];
        const [removed] = newWorkspaces.splice(draggedIndex, 1);
        newWorkspaces.splice(index, 0, removed);
        setLocalWorkspaces(newWorkspaces);
      }
    }
  };

  const handleDragLeave = () => {
    // Don't clear draggedOverIndex here to prevent flickering
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedWorkspace) {
      return;
    }

    // Update display_order based on final positions
    const workspaceIds = localWorkspaces.map(w => w.id);
    
    try {
      await updateWorkspaceOrder(workspaceIds);
    } catch (error) {
      console.error('Failed to update workspace order:', error);
      alert('Failed to update workspace order. Please try again.');
      // Revert to original order on error
      const filtered = workspaces
        .filter(w => w.org_id === orgId)
        .sort((a, b) => {
          if (a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return a.name.localeCompare(b.name);
        });
      setLocalWorkspaces(filtered);
    }

    // Reset drag state
    setDraggedWorkspace(null);
    setDraggedOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedWorkspace(null);
    setDraggedOverIndex(null);
  };

  const toggleWorkspaceExpanded = (workspaceId: string) => {
    setExpandedWorkspaces(prev => {
      const next = new Set(prev);
      if (next.has(workspaceId)) {
        next.delete(workspaceId);
      } else {
        next.add(workspaceId);
      }
      return next;
    });
  };

  const handleGenerateOverview = async (workspaceId: string) => {
    if (!token) return;

    setGeneratingOverview(prev => new Set(prev).add(workspaceId));

    try {
      const response = await fetch('/api/workspaces/generate-overview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ workspaceId })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate overview';
        const contentType = response.headers.get('content-type');
        
        try {
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            errorMessage = data.error || errorMessage;
            if (data.details) {
              errorMessage += `: ${data.details}`;
            }
          } else {
            // If not JSON, read as text
            const text = await response.text();
            console.error('Generate overview error:', text);
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          console.error('Error parsing response:', e);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Start polling for status
      pollOverviewStatus(workspaceId);
    } catch (error) {
      console.error('Error generating overview:', error);
      alert(error instanceof Error ? error.message : 'Failed to generate overview');
      setGeneratingOverview(prev => {
        const next = new Set(prev);
        next.delete(workspaceId);
        return next;
      });
    }
  };

  const pollOverviewStatus = async (workspaceId: string) => {
    if (!token) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/workspaces/overview-status/${workspaceId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setOverviewData(prev => ({
            ...prev,
            [workspaceId]: data
          }));

          // Stop polling if generation is complete or failed
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(pollInterval);
            setGeneratingOverview(prev => {
              const next = new Set(prev);
              next.delete(workspaceId);
              return next;
            });
          }
        }
      } catch (error) {
        console.error('Error polling overview status:', error);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 2 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setGeneratingOverview(prev => {
        const next = new Set(prev);
        next.delete(workspaceId);
        return next;
      });
    }, 120000);
  };

  const getOverviewSummary = (content: string | null) => {
    if (!content) return '';
    
    // Extract executive overview section
    const execMatch = content.match(/Executive Overview[\s\S]*?(?=\n(?:\d+\.|#|$))/i);
    if (execMatch) {
      const lines = execMatch[0].split('\n').filter(line => line.trim() && !line.includes('Executive Overview'));
      return lines.slice(0, 2).join(' ').trim();
    }
    
    // Fallback to first few lines
    const lines = content.split('\n').filter(line => line.trim());
    return lines.slice(0, 2).join(' ').trim();
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 w-48 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <div className="space-y-4">
        {localWorkspaces.map((workspace, index) => {
          const isExpanded = expandedWorkspaces.has(workspace.id);
          const overview = overviewData[workspace.id];
          const isGenerating = generatingOverview.has(workspace.id);
          const canGenerateOverview = userRole === 'Super-Admin' || userRole === 'Admin' || userRole === 'Editor';

          return (
            <div
              key={workspace.id}
              className={`bg-white border rounded-lg overflow-hidden transition-all duration-200 ${
                draggedWorkspace === workspace.id ? 'opacity-50 scale-105' : ''
              } ${
                draggedOverIndex === index ? 'border-blue-500 border-2' : 'border-gray-200'
              } flex`}
            >
              {/* Draggable left strip - hidden for now */}
              
              {/* Main content area */}
              <div className="flex-1">
                {/* Header section - always visible */}
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-lg font-medium text-gray-900">{workspace.name}</h4>
                        {workspace.is_default && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            Default
                          </span>
                        )}
                        {workspace.is_active ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                            Inactive
                          </span>
                        )}
                        {workspace.trial_ends_at && (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            new Date(workspace.trial_ends_at) > new Date()
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {new Date(workspace.trial_ends_at) > new Date()
                              ? `Trial ends ${new Date(workspace.trial_ends_at).toLocaleDateString()}`
                              : 'Trial expired - Activation requires payment confirmation'
                            }
                          </span>
                        )}
                      </div>
                      {workspace.description && (
                        <p className="mt-1 text-sm text-gray-600">{workspace.description}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {/* Workspace management controls - hidden for now */}
                      
                      {/* Expand/Collapse chevron */}
                      <button
                        onClick={() => toggleWorkspaceExpanded(workspace.id)}
                        className="p-1 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Overview section - always visible in collapsed state */}
                  {!isExpanded && overview?.status === 'completed' && overview.content && (
                    <div className="mt-3 text-sm text-gray-600 line-clamp-2">
                      {getOverviewSummary(overview.content)}
                    </div>
                  )}

                  {/* Overview actions */}
                  <div className="mt-4 flex items-center space-x-4">
                    {overview?.status === 'completed' ? (
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <SparklesIcon className="w-4 h-4" />
                        <span>Overview generated {overview.generatedAt ? formatDistanceToNow(new Date(overview.generatedAt), { addSuffix: true }) : 'recently'}</span>
                        {canGenerateOverview && (
                          <button
                            onClick={() => handleGenerateOverview(workspace.id)}
                            disabled={isGenerating}
                            className="ml-2 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          >
                            <ArrowPathIcon className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                      </div>
                    ) : overview?.status === 'failed' ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-red-600">Overview generation failed</span>
                        {canGenerateOverview && (
                          <button
                            onClick={() => handleGenerateOverview(workspace.id)}
                            disabled={isGenerating}
                            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    ) : isGenerating || overview?.status === 'pending' || overview?.status === 'generating' ? (
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span>Generating overview...</span>
                      </div>
                    ) : canGenerateOverview ? (
                      <button
                        onClick={() => handleGenerateOverview(workspace.id)}
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                      >
                        <SparklesIcon className="w-4 h-4 mr-1" />
                        Generate Overview
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t">
                    {/* Full overview content */}
                    {overview?.status === 'completed' && overview.content && (
                      <div className="p-6 bg-blue-50">
                        <h5 className="text-sm font-medium text-gray-700 mb-3">Workspace Overview</h5>
                        
                        {/* Key insights cards */}
                        {overview.keyInsights && overview.keyInsights.length > 0 && (
                          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {overview.keyInsights.map((insight, idx) => (
                              <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                                <div className="flex items-start space-x-2">
                                  {insight.type === 'pattern' && (
                                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                      </svg>
                                    </div>
                                  )}
                                  {insight.type === 'insight' && (
                                    <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center flex-shrink-0">
                                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                      </svg>
                                    </div>
                                  )}
                                  {insight.type === 'statistic' && (
                                    <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center flex-shrink-0">
                                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                      </svg>
                                    </div>
                                  )}
                                  <p className="text-sm text-gray-700">{insight.text}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="prose prose-sm max-w-none bg-white rounded-lg p-4">
                          <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(overview.content) }} />
                        </div>
                        
                        {overview.flowCount > 0 && (
                          <p className="mt-3 text-xs text-gray-500">
                            Based on {overview.flowCount} flow{overview.flowCount !== 1 ? 's' : ''} with AI summaries
                          </p>
                        )}
                      </div>
                    )}

                    {/* Prompt template */}
                    <div className="p-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Prompt Template</h5>
                      <div className="bg-gray-50 rounded p-3 text-sm text-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {workspace.prompt_template}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>


      {showModal && (
        <WorkspaceModal
          orgId={orgId}
          workspace={editingWorkspace}
          onClose={() => {
            setShowModal(false);
            setEditingWorkspace(null);
          }}
        />
      )}
    </div>
  );
}